import { getApplianceType } from 'src/helpers/device_settings_helpers';
import { consumption_add_total } from 'src/helpers/utilities';
import type { Device } from 'src/types/Device';
import type { ConsumptionStores } from './consumption_helpers';
import { getRealtimeConsumptionMap } from './consumption_helpers';
import { STANDBY_POWER_THRESHOLD_W } from './energy_insight_metrics';
import { getCurrentPvGenerationFlow, type PvGenerationFlow } from './pv_flow_helpers';

export type LiveEnergyConsumer = {
	id: string;
	title: string;
	power: number;
	usage: string;
	roomId?: number;
	icon?: string;
	backgroundColor?: unknown;
	image?: string;
};

export type LiveEnergySnapshot = {
	source: 'pv_setup' | 'device_tags';
	loadW: number;
	solarW: number;
	solarToHomeW: number;
	gridImportW: number;
	exportW: number;
	standbyW: number;
	selfPoweredPct: number;
	gridExposurePct: number;
	consumers: LiveEnergyConsumer[];
	topConsumer: LiveEnergyConsumer | null;
	byAppliance: Record<string, number>;
};

type ConsumptionEntry = {
	id?: string | number;
	title?: string;
	_power?: number;
	in_power?: number;
	ex_power?: number;
	room_id?: number;
	icon?: string;
	backgroundColor?: unknown;
	image?: string;
};

const absolutePower = (value?: number) => Math.abs(Number(value ?? 0));

export const buildLiveEnergySnapshot = (
	stores: ConsumptionStores,
	devices: Record<string, Device>,
	pvFlow: PvGenerationFlow | null = getCurrentPvGenerationFlow(),
): LiveEnergySnapshot => {
	const entries = getRealtimeConsumptionMap(stores, '', 'by_device_account') as Record<
		string,
		ConsumptionEntry
	>;
	const consumers: LiveEnergyConsumer[] = [];
	const byAppliance: Record<string, number> = {};
	let taggedSolarW = 0;
	let taggedGridImportW = 0;
	let taggedExportW = 0;
	let consumerLoadW = 0;
	let standbyW = 0;

	Object.entries(entries).forEach(([deviceId, entry]) => {
		const device = devices[deviceId];
		if (!device) return;
		const signedPower = Number(entry._power ?? entry.in_power ?? 0);
		const power = absolutePower(signedPower);
		const usage = getApplianceType(device).usage;

		if (usage === 'generated_energy') {
			taggedSolarW += Math.max(signedPower, 0);
			return;
		}
		if (usage === 'grid_source') {
			taggedGridImportW += Math.max(signedPower, 0);
			taggedExportW += Math.max(-signedPower, 0);
			return;
		}
		if (power <= 0) return;

		const consumer: LiveEnergyConsumer = {
			id: deviceId,
			title: entry.title ?? device.name ?? deviceId,
			power,
			usage,
			roomId: device.room_id ?? entry.room_id,
			icon: entry.icon,
			backgroundColor: entry.backgroundColor,
			image: entry.image,
		};
		consumers.push(consumer);
		consumerLoadW += power;
		byAppliance[usage] = (byAppliance[usage] ?? 0) + power;
		if (power <= STANDBY_POWER_THRESHOLD_W) standbyW += power;
	});

	if (consumerLoadW <= 0) {
		const roomEntries = Object.values(
			getRealtimeConsumptionMap(stores, '', 'account') as Record<string, ConsumptionEntry>,
		);
		consumerLoadW = consumption_add_total(roomEntries, 'in_power');
	}

	consumers.sort((a, b) => b.power - a.power);

	// Control treats explicitly tagged source meters as authoritative. PV topology
	// calculations remain a fallback for accounts that do not expose those meters
	// in the real-time consumption store.
	const hasTaggedSources = taggedSolarW > 0 || taggedGridImportW > 0 || taggedExportW > 0;
	const source = hasTaggedSources || !pvFlow?.hasPvSetup ? 'device_tags' : 'pv_setup';
	const solarW = hasTaggedSources ? taggedSolarW : Math.max(pvFlow?.solarPower ?? 0, 0);
	const gridImportW = hasTaggedSources ? taggedGridImportW : Math.max(pvFlow?.gridImport ?? 0, 0);
	const exportW = hasTaggedSources ? taggedExportW : Math.max(pvFlow?.exportPower ?? 0, 0);
	const measuredLoadW = Math.max(solarW + gridImportW - exportW, 0);
	const loadW = hasTaggedSources
		? measuredLoadW || consumerLoadW
		: Math.max(consumerLoadW, pvFlow?.homeConsumption ?? 0);
	const solarToHomeW = Math.min(
		solarW,
		Math.max(
			loadW - gridImportW,
			hasTaggedSources ? solarW - exportW : (pvFlow?.solarToHome ?? 0),
			0,
		),
	);
	const unknownW = Math.max(loadW - consumerLoadW, 0);
	if (unknownW > 0) byAppliance.unknown = (byAppliance.unknown ?? 0) + unknownW;
	const selfPoweredPct =
		loadW > 0
			? Math.min(Math.round((Math.min(solarToHomeW, loadW) / loadW) * 100), 100)
			: solarW + gridImportW > 0
				? Math.min(Math.round((solarW / (solarW + gridImportW)) * 100), 100)
				: 0;

	return {
		source,
		loadW,
		solarW,
		solarToHomeW,
		gridImportW,
		exportW,
		standbyW,
		selfPoweredPct,
		gridExposurePct: loadW > 0 ? Math.max(0, 100 - selfPoweredPct) : 0,
		consumers,
		topConsumer: consumers[0] ?? null,
		byAppliance,
	};
};
